import React, { useEffect, useMemo, useRef, useState } from "react";
import "./style.css";
import { CustomPopUpModal, OptionList, Switcher, Title } from "../../Atoms";
import { Tooltip, useToast, Spinner, Button, filter } from "@chakra-ui/react";
import { CopyIcon, DownloadIcon } from "../../../Assets/images/svgs";
import { ReactTable } from "../../Molecules";
import { getOrderDetails, getShipmentDetails } from "../../../APIs";
import { getDate, getLocalisedDate } from "../../../Library/datetime";
import { downloadFileFromUrl, ellipsisify, getLocalisedNumber } from "../../../Library/helpers";
import { SHOW_FILTERED_ORDER_HEADERS, SHOW_ORDER_HEADERS, SHOW_SHIPMENT_HEADERS } from "../../../Library/tableHeaders";
import { getFilters } from "../../../Library/tableHelpers";
import { Link } from "react-router-dom";
import { ORDER_STATUS, ORDER_STATUS_DEVICE_ADDED_IN_STOCK, ORDER_STATUS_VAL, SHIPMENT_STATUS, SHIPMENT_STATUS_REQUESTED, SHIPPING_STATUSES, SUBDEALERS_DES } from "../../../Library/constants";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";
import PATHS from "../../../Routes/paths";
import Zip from "../../../Library/zip";

const ORDER_WISE = "order-wise";
const IMEI_WISE = "imei-wise";
const CUSTOM = "custom";
const ALL = "all";

const OrdersTable = ({ filterHeaders = false, dealerId, hideDealerNames = false, title, shipment, des, allOrdersData, shipmentData, showSelection, showSelectAll, checkboxLabel, onRowSelect, onDownload, selectedShipments, downloadLoader, hideTableFilters, hideSearch, rowCount, hideColumn, ellipsify, isDashboard, bulkDownload, prefix }) => {
  const [ orders, setOrders ] = useState([]);
  const [ selectedTableFilter, setSelectedTableFilter ] = useState(1);
  const ordersCountAsPerStatus = useRef(shipment ? { 1: [], 2: [], 3: [], total: [] } : { 1: [], 1.1:[], 2: [], 3: [], 4: [], 5: [], total: [] });
  const DESIGNATION = JSON.parse(window.localStorage.getItem("des"));
  const Toast = useToast();
  const [invoicesLoader, setInvoicesLoader] = useState(false);
  const [modalData, setModalData] = useState({});

  const processAndStoreData = (data, isShipment) => {
    if (!data || !data.orders || data.orders.length === 0) return console.error("no orders found");

    const orders = data.orders;
    const STATUS = isShipment ? SHIPMENT_STATUS : ORDER_STATUS;

    orders.forEach(order => {
      const { os, amt, cdt, devcnt, devdsptch } = order;

      if (amt) order.amt = getLocalisedNumber(amt);
      if (cdt) order.cdt = getLocalisedDate(cdt, true);
      if (devcnt) order.devcnt = devcnt;
      if (devdsptch) order.devdsptch = devdsptch;

      if (os && os.length > 0) {
        const latestStatus = os.filter(stat => stat.ts).pop();
        if (latestStatus) {
          const { stat } = latestStatus;
          order.currentState = STATUS[stat];
          order.state = stat;

          if (!ordersCountAsPerStatus.current[stat]) {
            ordersCountAsPerStatus.current[stat] = [];
          }

          if (devcnt && devdsptch && devdsptch > 0 && devdsptch < devcnt) {
            order.currentState = `Partially added in stock (${devdsptch}/${devcnt})`;
            ordersCountAsPerStatus.current[ORDER_STATUS_DEVICE_ADDED_IN_STOCK].push(order);
            return;
          }

          ordersCountAsPerStatus.current[stat].push(order);
        }
      }

      order.vnm = "Ajjas";
    });

    ordersCountAsPerStatus.current.total = orders;
    setOrders((rowCount && orders.length) ? orders.splice(0, rowCount) : orders);
  }

  useEffect(() => {
    if (orders && orders.length && allOrdersData && allOrdersData.orders && allOrdersData.orders.length === orders.length) return;

    const handleData = (data, processFunction) => {
      if (data && data.orders && data.orders.length) {
        processFunction(data);
      } else {
        const callback = (err, fetchedData) => {
          if (err) console.error(err);
          else processFunction(fetchedData);
        };

        if (shipment) getShipmentDetails(null, dealerId || null, null, [], callback)
        else getOrderDetails(null, dealerId || null, null, callback);
      }
    };

    if (shipment) handleData(shipmentData, (data) => processAndStoreData(data, true));
    else handleData(allOrdersData, (data) => processAndStoreData(data, false));
  }, [dealerId, shipment, allOrdersData, shipmentData]);


  const columns = useMemo(() => {
    let TABLE_HEADERS = SHOW_ORDER_HEADERS
    if (filterHeaders) TABLE_HEADERS = SHOW_FILTERED_ORDER_HEADERS;
    if (shipment) TABLE_HEADERS = SHOW_SHIPMENT_HEADERS;

    if (hideColumn) {
      TABLE_HEADERS = { ...TABLE_HEADERS };
      hideColumn.forEach(column => delete TABLE_HEADERS[column]);
    }

    const cols = [];
    Object.values(TABLE_HEADERS).forEach(header => {
      if (hideDealerNames && header.key === "dnm") return;

      cols.push({
        Header: header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        ...getFilters(header),
        Cell: (row) => {
          if (row.column.id === "dnm") {
            if (ellipsify) {
              return (
                <Tooltip label={row.value && row.value.length <= 14 ? "" : row.value}>
                  <span className="table-cell-nowrap">
                    {ellipsisify(row.value, 14)}
                  </span>
                </Tooltip>
              )
            }

            return <span className="table-cell-nowrap">{row.value}</span>
          }

          if (row.column.id === "orderId") {
            const getPath = (shipment, DESIGNATION, row) => {
              const basePath = shipment ? PATHS.SHIPMENT_OVERVIEW : "";
              const orderPath = DESIGNATION !== SUBDEALERS_DES ? basePath : "";
              const detailPath = `${orderPath}${PATHS.ORDERS_OVERVIEW}/${PATHS.ORDER_DETAIL}?`;
              const queryParam = shipment ? `shipId=${row.value}` : `idx=${row.value}`;

              return `${detailPath}${queryParam}`;
            };

            return (
              <Link className="order-id-cell-value" to={getPath(shipment, DESIGNATION, row)}>
                  {row.value || '-'}
              </Link>
            );
          }

          if (row.column.id === "transc") {
            return (row.value && row.value.amt) || "-"
          }

          if (row.column.id === "currentState" && isDashboard && ellipsify) {
            return (
              <Tooltip label={row.value && row.value.length > 12 ? row.value : ""}>
                {ellipsisify(row.value, 12)}
              </Tooltip>
            )
          }

          return row.value
        },
      });
    });

    return cols;
  }, [filterHeaders, hideDealerNames]);

  const TABLE_FILTER_OPTIONS = [
    { idx: 1, dataKey: "total", label: `All (${ordersCountAsPerStatus.current["total"].length})`, total: ordersCountAsPerStatus.current["total"].length },
    { idx: 2, dataKey: "1", label: `Placed (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Placed"]].length})`, total: ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Placed"]].length },
    { idx: 3, dataKey: "1.1", label: `PI Raised (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["PI Raised"]]?.length})`, total: ordersCountAsPerStatus.current[ORDER_STATUS_VAL["PI Raised"]]?.length },
    { idx: 4, dataKey: "2", label: `Cancelled (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Cancelled"]].length})`, total: ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Cancelled"]].length },
    { idx: 5, dataKey: "3", label: `Payment Done (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Payment Done"]].length})`, spacing: true, total: ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Payment Done"]].length },
    { idx: 6, dataKey: "4", label: `Accepted (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Accepted"]]?.length})`, total: ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Accepted"]]?.length },
    { idx: 7, dataKey: "5", label: `Added In Stock (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Added In Stock"]]?.length})`, spacing: true, total: ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Added In Stock"]]?.length },
  ]

  const SHIPMENT_FILTER_OPTIONS = [
    { idx: 1, dataKey: "total", label: `All (${ordersCountAsPerStatus.current["total"].length})`, total: ordersCountAsPerStatus.current["total"].length },
    { idx: 2, dataKey: "1", label: `Requested (${ordersCountAsPerStatus.current[SHIPPING_STATUSES["Requested"]].length})`, total: ordersCountAsPerStatus.current[SHIPPING_STATUSES["Requested"]].length },
    { idx: 3, dataKey: "2", label: `Shipped (${ordersCountAsPerStatus.current[SHIPPING_STATUSES["Shipped"]].length})`, total: ordersCountAsPerStatus.current[SHIPPING_STATUSES["Shipped"]].length },
    { idx: 4, dataKey: "3", label: `Delivered (${ordersCountAsPerStatus.current[SHIPPING_STATUSES["Delivered"]].length})`, total: ordersCountAsPerStatus.current[SHIPPING_STATUSES["Delivered"]].length },
  ]

  const getOptions = () => {
    const filterOptions = shipment ? SHIPMENT_FILTER_OPTIONS : TABLE_FILTER_OPTIONS;

    let options = filterOptions.filter((option) => {
      if (option.total) return true;
      return false;
    });

    options.sort((a, b) => {
      if (a.total > b.total) return -1;
      if (a.total < b.total) return 1;
      return 0;
    })

    return options;
  };

  const handleFiltersTabChange = (tab) => {
    setSelectedTableFilter(tab.idx);
    setOrders(ordersCountAsPerStatus.current[tab.dataKey]);
  }

  const downloadData = (data) => {
    if(!data.length) return;

    var sendToExcel = data.map(a => Object.assign({}, a));
    sendToExcel.forEach((order, idx) => {
      order["Sr No"] = idx + 1;
      order["Order ID"] = order.orderId || "-";
      order["Order Date"] = getDate(order.cdt, null, "/") || "-";
      order["Order Status"] = order.currentState || "-";
      order["Quantity"] = order.devcnt || "-";
      order["Dealer"] = order.dnm || "-";
      order["Vendor"] = order.vnm || "-";
      order["IMEIs"] = (order.imeis && order.imeis.length) ? order.imeis.join(", ") : "-";
      order["Amount"] = (!shipment && order.transc && order.transc.amt) ? order.transc.amt : "-";
      order["Placed"] = !shipment && order.os[0] && order.os[0].ts ? getDate(order.os[0].ts, true) : "-";
      order["Accepted"] = !shipment && order.os[1] && order.os[1].ts ? getDate(order.os[1].ts, true) : "-";
      order["Cancelled"] = !shipment && order.os[2] && order.os[2].ts ? getDate(order.os[2].ts, true) : "-";
      order["Shipped"] = !shipment && order.os[3] && order.os[3].ts ? getDate(order.os[3].ts, true) : "-";
      order["Delivered"] = !shipment && order.os[4] && order.os[4].ts ? getDate(order.os[4].ts, true) : "-";
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Order ID, Order Date, Order Status, Quantity, Dealer, Vendor, IMEIs, Amount, Placed, Accepted, Cancelled, Shipped, Delivered";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Orders_" + Date.now(), false);
  }

  const handleRowSelect = (_, selectedRows) => {
    if (onRowSelect) onRowSelect(selectedRows);
  }

  const handleCopy = (textToCopy) => {
    if (!textToCopy) return;

    navigator.clipboard.writeText(textToCopy);
    Toast({ isClosable: true, description: "Copied", position: "bottom", variant: "subtle", duration: 1500 })
  }

  const getValidSelectedShipments = () => {
    if (selectedShipments && selectedShipments.length) {
      return selectedShipments.filter(item => !item?.original?.disabled);
    }
    return [];
  }

  const handleInvoiceDownload = async (option) => {
    if (option.disabled) return;
    setInvoicesLoader(true);

    let noInvoiceIds = [];
    let items = [];
    if (option.value === CUSTOM) items = getValidSelectedShipments();
    else items = orders.filter(item => !item.disabled);

    const urls = items.reduce((acc, item) => {
      const invpdf = option.value === CUSTOM ? item?.original?.invpdf : item.invpdf;
      const dnm = option.value === CUSTOM ? item?.original?.dnm : item.dnm;
      const orderId = option.value === CUSTOM ? item?.original?.orderId : item.orderId;

      if (invpdf) {
        const url = `${prefix}/${invpdf}`;
        let fileName = dnm && orderId ? `${dnm}_${orderId}.pdf` : invpdf;
        if (fileName.includes("/")) fileName = fileName.replaceAll("/","");
        acc.push({ url, fileName });
      } else noInvoiceIds.push(orderId);

      return acc;
    }, []);

    if (urls.length) {
      if (urls.length === 1) {
        let { url, fileName } = urls[0];
        downloadFileFromUrl(url, fileName);
      }
      else {
        let zip = new Zip("Ajjas IOT Invoices");
        zip.fetch2zip(urls, "/")
          .then(() => zip.makeZip())
          .catch(err => {
            console.error(err);
            Toast({ isClosable: true, description: "Failed to download invoices.", position: "bottom", duration: 3000 });
          });
      }
    } else Toast({ isClosable: true, description: "No invoices found to download.", position: "bottom", duration: 3000 });

    if (noInvoiceIds.length) {
      const value = noInvoiceIds.join(", ");
      const message = noInvoiceIds.length > 1 ? "Invoices for the following IDs are not available:" : "Invoice for the following ID is not available:";
      setModalData({
        open: true,
        closeBtn: "Close",
        onModalClose: () => setModalData({}),
        confirmBtn: "Copy",
        onConfirm: () => handleCopy(value),
        modalBody: (
          <div className="modal-info">
            <p className="modal-heading">{message}</p>
            <Tooltip label={value.length > 20 ? value : ""}>
              <p className="modal-text">
                {ellipsisify(value, 20)}
              </p>
            </Tooltip>
          </div>
        ),
      });
    }
    setInvoicesLoader(false);
  }

  const handleReportsDownload = (option) => {
    if (option.disabled) return;

    if (option.value === ORDER_WISE) downloadData(orders);
    else if (option.value === IMEI_WISE) onDownload(getValidSelectedShipments());
  }

  const getSelectAllStatus = () => {
    const validRows = orders.filter(item => !item.disabled);
    if (validRows.length && getValidSelectedShipments().length &&  validRows.length === getValidSelectedShipments().length) return true;
    else return false;
  }

  const getInvoicesDownloadOptions = () => {
    const isSelectAll = getSelectAllStatus();
    const validSelectedCount = getValidSelectedShipments().length;

    return [
      { value: ALL, label: "Download All", disabled: false },
      {
        value: CUSTOM,
        isSelectAll,
        label: `Download Selected (${validSelectedCount})`,
        disabled: validSelectedCount === 0,
        tooltipText: "please select at least one shipment",
        showTooltip: validSelectedCount === 0,
      }
    ];
  };

  const filteredInvoicesDownloadOptions = getInvoicesDownloadOptions().filter(option => !option.isSelectAll);

  const ReportsDownloadOptions = [
    { value: ORDER_WISE, label: `All Shipment Report` },
    { value: IMEI_WISE, label: `IMEI Report (${getValidSelectedShipments().length})`, disabled: getValidSelectedShipments().length === 0, tooltipText: "please select at least one shipment" , showTooltip: getValidSelectedShipments().length === 0},
  ]

  return (
    <section className="orders-table">
      <article className="order-details-table-container">
       <div className="table-title-cnt">
         <Title variant="md" fixed>{title || (DESIGNATION === SUBDEALERS_DES ? "Shipment Details" : "Order Details")}</Title>
         {bulkDownload ? <div className="dropdown-cnt">
           {invoicesLoader ? <Spinner className="loader"/> : <OptionList name="Download Invoices" Icon={DownloadIcon} options={filteredInvoicesDownloadOptions} onOptionClick={handleInvoiceDownload}/>}
           <OptionList name="Download Reports" Icon={DownloadIcon} options={ReportsDownloadOptions} onOptionClick={handleReportsDownload}/>
         </div> : null}
       </div>
        {!hideTableFilters ? <div className="order-table-filters-row">
          <Switcher
            className={`table-filters-switcher ${getOptions().length < 2 ? "table-filters-switcher-new" : ""}`}
            options={getOptions()}
            selectedOption={selectedTableFilter}
            handleChange={handleFiltersTabChange}
          />
          { !bulkDownload ?
            <Button disabled={downloadLoader} onClick={() => downloadData(orders)} className="download-order-btn">
             {downloadLoader ? <Spinner color="#007AFF" /> : <DownloadIcon />}
            </Button> : null
          }
        </div> : null}
        <ReactTable
          columns={columns}
          data={orders}
          showSearch={!hideSearch}
          pageSize={10}
          showSelection={showSelection}
          showSelectAll={showSelectAll}
          checkboxLabel={checkboxLabel}
          onRowSelect={handleRowSelect}
        />
      </article>
      <CustomPopUpModal {...modalData} />
    </section>
  )
}

export default OrdersTable